<template>
  <div class="reporting-section">
    <div class="section-header">
      <i class="material-icons section-icon">compare_arrows</i>
      <h2>{{ $t('dash.reporting.section.concurrent.title') }}</h2>
      <button class="add-btn" @click="showSearch = !showSearch" :class="{ active: showSearch }">
        <i class="material-icons">{{ showSearch ? 'close' : 'add' }}</i>
        {{ $t('dash.reporting.section.concurrent.add') }}
      </button>
    </div>

    <!-- Search panel -->
    <div class="search-panel" v-if="showSearch">
      <div class="search-input-wrap">
        <i class="material-icons search-icon">search</i>
        <input
          ref="searchInput"
          class="search-input"
          type="text"
          v-model="searchQuery"
          :placeholder="searchPlaceholder"
          @input="onSearchInput"
          @keydown.escape="showSearch = false"
        >
        <div class="search-spinner" v-if="searchLoading">
          <div class="input-loader"></div>
        </div>
      </div>
      <div class="search-results" v-if="!searchLoading && searchResults.length">
        <div
          class="search-result"
          v-for="(r, i) in searchResults"
          :key="i"
          @click="selectResult(r)"
        >
          <div class="result-thumb">
            <img v-if="r.image" :src="r.image" alt="">
            <i v-else class="material-icons">account_circle</i>
          </div>
          <div class="result-info">
            <div class="result-row1">
              <span class="result-name">{{ r.name || r.unique_id }}</span>
              <span v-if="r.unique_id" class="result-handle">@{{ r.unique_id }}</span>
              <span v-if="r.id && !r.unique_id" class="result-handle">{{ r.id }}</span>
            </div>
            <div v-if="r.description" class="result-desc">{{ r.description }}</div>
          </div>
          <i class="material-icons result-add">add_circle_outline</i>
        </div>
      </div>
      <div class="search-loading-results" v-else-if="searchLoading">
        <div class="search-skeleton" v-for="i in 3" :key="i">
          <div class="skel-thumb"></div>
          <div class="skel-lines">
            <div class="skel-line skel-line--wide"></div>
            <div class="skel-line skel-line--narrow"></div>
          </div>
        </div>
      </div>
      <div class="search-empty" v-else-if="searchQuery.length >= 2 && !searchLoading">
        {{ $t('dash.reporting.section.concurrent.no_results') }}
      </div>
    </div>

    <div class="post-empty" v-if="!hasAnyConcurrent">
      <i class="material-icons">group_add</i>
      <p>{{ $t('dash.reporting.section.concurrent.empty') }}</p>
    </div>

    <template v-else>
      <div class="post-empty" v-if="loading">
        <i class="material-icons spin">hourglass_empty</i>
        <p>{{ $t('dash.reporting.section.concurrent.loading') }}</p>
      </div>

      <div class="post-empty" v-else-if="error">
        <i class="material-icons" style="color:#e0d0ea">error_outline</i>
        <p style="color:#c0392b">{{ error }}</p>
        <button class="page-btn" style="width:auto;padding:0 14px" @click="load">{{ $t('dash.reporting.retry') }}</button>
      </div>

      <template v-else-if="allRows.length">
        <div class="concurrent-table">

          <!-- En-tête -->
          <div class="table-head" :style="{ gridTemplateColumns: gridTemplate }">
            <span class="col-header sortable" :class="sortClass('name')" @click="onSort('name')">
              {{ cap($t('dash.reporting.section.concurrent.page')) }}<i class="material-icons sort-icon">{{ sortIcon('name') }}</i>
            </span>
            <span class="col-header sortable align-right" :class="sortClass('followers')" @click="onSort('followers')">
              {{ cap($t('dash.reporting.section.concurrent.followers')) }}<i class="material-icons sort-icon">{{ sortIcon('followers') }}</i>
            </span>
            <span class="col-header sortable align-right" :class="sortClass('posts')" @click="onSort('posts')">
              {{ cap($t('dash.reporting.section.concurrent.posts')) }}<i class="material-icons sort-icon">{{ sortIcon('posts') }}</i>
            </span>
            <span class="col-header sortable align-right" :class="sortClass('reactions')" @click="onSort('reactions')">
              {{ cap($t('dash.reporting.pdf.listItem.reactions')) }}<i class="material-icons sort-icon">{{ sortIcon('reactions') }}</i>
            </span>
            <span class="col-header sortable align-right" :class="sortClass('comments')" @click="onSort('comments')">
              {{ cap($t('dash.reporting.pdf.listItem.comments')) }}<i class="material-icons sort-icon">{{ sortIcon('comments') }}</i>
            </span>
            <span v-if="network === 'facebook'" class="col-header sortable align-right" :class="sortClass('shares')" @click="onSort('shares')">
              {{ cap($t('dash.reporting.pdf.listItem.shares')) }}<i class="material-icons sort-icon">{{ sortIcon('shares') }}</i>
            </span>
            <Tooltip :text="$t('dash.reporting.tooltip.concurrent_engagement_score')" position="bottom">
              <span class="col-header sortable align-right" :class="sortClass('engagement')" @click="onSort('engagement')">
                {{ cap($t('dash.reporting.section.concurrent.engagement')) }}<i class="material-icons sort-icon">{{ sortIcon('engagement') }}</i>
              </span>
            </Tooltip>
            <span class="col-header"></span>
          </div>

          <!-- Groupes ligne + expansion -->
          <div v-for="row in sortedRows" :key="row.network_id" class="row-group">

            <div
              class="table-row"
              :class="{ 'row--own': row.isOwnPage, 'row--pending': row.pending, 'row--error': row.error }"
              :style="{ gridTemplateColumns: gridTemplate }"
              @click="!row.pending && !row.error && !row.isOwnPage && toggle(row.network_id)"
            >
              <div class="cell-page">
                <div class="page-thumb">
                  <img
                    v-if="rowPicture(row) && !brokenImages[row.network_id]"
                    :src="rowPicture(row)"
                    alt=""
                    @error="onImgError(row.network_id)"
                  >
                  <i v-else class="material-icons">account_circle</i>
                </div>
                <span class="page-name">{{ row.concurrent_name || row.network_id }}</span>
                <span v-if="row.pending" class="type-badge badge-pending">
                  <i class="material-icons spin" style="font-size:10px;margin-right:2px">hourglass_empty</i>
                  {{ $t('dash.reporting.section.concurrent.pending') }}
                </span>
                <Tooltip v-if="row.error" :text="row.error_message || ''" position="top">
                  <span class="type-badge badge-error">
                    <i class="material-icons" style="font-size:10px;margin-right:2px">error_outline</i>
                    {{ $t(row.error === 'not_found' ? 'dash.reporting.section.concurrent.not_found' : 'dash.reporting.section.concurrent.unavailable') }}
                  </span>
                </Tooltip>
                <i v-if="!row.pending && !row.error && !row.isOwnPage" class="material-icons expand-icon">
                  {{ expanded[row.network_id] ? 'expand_less' : 'expand_more' }}
                </i>
              </div>
              <span class="cell-num">{{ row.pending ? '…' : row.error ? '—' : fmt(rowFollowers(row)) }}</span>
              <span class="cell-num">{{ row.pending ? '…' : row.error ? '—' : fmt(rowPostsCount(row)) }}</span>
              <span class="cell-num">{{ row.pending ? '…' : row.error ? '—' : fmt(rowReactions(row)) }}</span>
              <span class="cell-num">{{ row.pending ? '…' : row.error ? '—' : fmt(rowComments(row)) }}</span>
              <span v-if="network === 'facebook'" class="cell-num">{{ row.pending ? '…' : row.error ? '—' : fmt(rowShares(row)) }}</span>
              <span class="cell-num">{{ row.pending ? '…' : row.error ? '—' : rowEngagement(row) }}</span>
              <div class="cell-actions">
                <button
                  v-if="!row.isOwnPage"
                  class="delete-btn"
                  :class="{ loading: removeLoading[row.network_id] }"
                  :disabled="!!removeLoading[row.network_id]"
                  @click.stop="removeConcurrent(row)"
                  :title="$t('dash.reporting.section.concurrent.remove')"
                >
                  <i class="material-icons">{{ removeLoading[row.network_id] ? 'hourglass_empty' : 'close' }}</i>
                </button>
              </div>
            </div>

            <!-- Posts dépliés -->
            <div class="sub-section" v-if="!row.isOwnPage && expanded[row.network_id]">
              <template v-if="rowPosts(row).length">
                <div class="post-head" :style="{ gridTemplateColumns: postGridTemplate }">
                  <span class="col-header sortable" :class="postSortClass(row.network_id, 'date')" @click="onPostSort(row.network_id, 'date')">
                    {{ cap($t('dash.reporting.pdf.listItem.date')) }}<i class="material-icons sort-icon">{{ postSortIcon(row.network_id, 'date') }}</i>
                  </span>
                  <span class="col-header">{{ cap($t('dash.reporting.pdf.listItem.publication')) }}</span>
                  <span class="col-header sortable align-right" :class="postSortClass(row.network_id, 'reactions')" @click="onPostSort(row.network_id, 'reactions')">
                    {{ cap($t('dash.reporting.pdf.listItem.reactions')) }}<i class="material-icons sort-icon">{{ postSortIcon(row.network_id, 'reactions') }}</i>
                  </span>
                  <span class="col-header sortable align-right" :class="postSortClass(row.network_id, 'comments')" @click="onPostSort(row.network_id, 'comments')">
                    {{ cap($t('dash.reporting.pdf.listItem.comments')) }}<i class="material-icons sort-icon">{{ postSortIcon(row.network_id, 'comments') }}</i>
                  </span>
                  <span v-if="network === 'facebook'" class="col-header sortable align-right" :class="postSortClass(row.network_id, 'shares')" @click="onPostSort(row.network_id, 'shares')">
                    {{ cap($t('dash.reporting.pdf.listItem.shares')) }}<i class="material-icons sort-icon">{{ postSortIcon(row.network_id, 'shares') }}</i>
                  </span>
                </div>
                <div class="post-row" v-for="(p, i) in paginatedPosts(row)" :key="i" :style="{ gridTemplateColumns: postGridTemplate }">
                  <span class="cell-date">{{ postDate(p) }}</span>
                  <div class="cell-post">
                    <p class="post-msg">{{ postMessage(p) }}</p>
                    <a v-if="postLink(p)" :href="postLink(p)" target="_blank" class="post-link" @click.stop>
                      <i class="material-icons">open_in_new</i>
                    </a>
                  </div>
                  <span class="cell-num">{{ fmt(postReactions(p)) }}</span>
                  <span class="cell-num">{{ fmt(postComments(p)) }}</span>
                  <span v-if="network === 'facebook'" class="cell-num">{{ fmt(postShares(p)) }}</span>
                </div>
                <div class="pagination" v-if="postPageCount(row) > 1">
                  <button class="page-btn" :disabled="getPage(row.network_id) === 1" @click="setPage(row.network_id, getPage(row.network_id) - 1)">
                    <i class="material-icons">chevron_left</i>
                  </button>
                  <span class="page-info">{{ getPage(row.network_id) }} / {{ postPageCount(row) }}</span>
                  <button class="page-btn" :disabled="getPage(row.network_id) === postPageCount(row)" @click="setPage(row.network_id, getPage(row.network_id) + 1)">
                    <i class="material-icons">chevron_right</i>
                  </button>
                </div>
              </template>
              <div v-else class="post-empty" style="padding:20px 0">
                <i class="material-icons">inbox</i>
                <p>{{ $t('dash.reporting.no_posts') }}</p>
              </div>
            </div>

          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import Tooltip from '../tooltip/tooltipInit.vue'

var PAGE_SIZE = 20

export default {
  name: 'ReportingConcurrent',
  components: { Tooltip },

  props: {
    page: { type: Object, default: null },
    stats: { type: Object, default: null },
    dateStart: { type: String, default: '' },
    dateEnd: { type: String, default: '' },
  },

  data() {
    return {
      // Display data
      loading: false,
      error: null,
      concurrents: [],
      expanded: {},
      retryTimer: null,
      sortKey: 'followers',
      sortDir: 'desc',
      postSort: {},
      pagination: {},
      // Images ayant échoué au chargement (fallback sur l'icône)
      brokenImages: {},
      // Management
      localConcurrents: [],
      showSearch: false,
      searchQuery: '',
      searchResults: [],
      searchLoading: false,
      searchDebounce: null,
      removeLoading: {},
      addLoading: false,
    }
  },

  computed: {
    network() {
      return this.page && this.page.network ? this.page.network : ''
    },
    hasAnyConcurrent() {
      return this.localConcurrents.length > 0
    },
    searchPlaceholder() {
      var map = {
        facebook: 'Nom de la page Facebook',
        instagram: 'Nom d\'utilisateur Instagram',
        linkedin: 'Nom de l\'organisation LinkedIn',
        tiktok: 'Nom d\'utilisateur TikTok',
      }
      return map[this.network] || 'Rechercher...'
    },
    gridTemplate() {
      var cols = ['1fr', '110px', '80px', '95px', '105px']
      if (this.network === 'facebook') cols.push('85px')
      cols.push('105px', '32px')
      return cols.join(' ')
    },
    postGridTemplate() {
      var cols = ['90px', '1fr', '95px', '105px']
      if (this.network === 'facebook') cols.push('85px')
      return cols.join(' ')
    },
    ownPageRow() {
      if (!this.page || !this.stats || !this.stats.page) return null
      var p = this.stats.page
      var net = this.page.network
      var nid = this.page.network_id || this.page.id
      var posts = (this.stats && this.stats.posts) || []
      var reactionsFromPosts = posts.reduce(function(s, post) {
        return s + (post.post_reactions_sum || post.reactions_count || post.like_count || 0)
      }, 0)
      return {
        network_id: '__own__',
        network: net,
        concurrent_name: this.page.name || null,
        concurrent_picture: this._freshPicture(net, nid, this.page.avatar),
        pageFollowers: p.page_fans || p.followers_count || 0,
        postsCount: p.nb_posts || 0,
        total_reactions: p.totalReactions || reactionsFromPosts,
        total_comments: p.totalComments || 0,
        total_shares: p.totalShares || 0,
        posts: null,
        isOwnPage: true,
      }
    },
    allRows() {
      var rows = []
      if (this.ownPageRow) rows.push(this.ownPageRow)
      return rows.concat(this.concurrents)
    },
    sortedRows() {
      var self = this
      var dir = this.sortDir === 'asc' ? 1 : -1
      var key = this.sortKey
      // Page courante triée dans le lot — pas de position forcée
      return this.allRows.slice().sort(function(a, b) {
        var va = self.sortVal(a, key)
        var vb = self.sortVal(b, key)
        if (va < vb) return -1 * dir
        if (va > vb) return 1 * dir
        return 0
      })
    },
  },

  watch: {
    page: function(newPage, oldPage) {
      var newId = newPage && newPage.id
      var oldId = oldPage && oldPage.id
      if (newId !== oldId) {
        this.initLocalConcurrents()
        this.concurrents = []; this.expanded = {}; this.clearRetry()
      }
    },
    dateStart: function() { this.concurrents = []; this.expanded = {}; this.clearRetry() },
    dateEnd: function() { this.concurrents = []; this.expanded = {}; this.clearRetry() },
    showSearch: function(val) {
      if (val) {
        this.$nextTick(function() { if (this.$refs.searchInput) this.$refs.searchInput.focus() })
        this.searchQuery = ''; this.searchResults = []
      }
    },
  },

  mounted: function() {
    this.initLocalConcurrents()
    if (this.hasAnyConcurrent) this.load()
  },

  beforeDestroy: function() {
    this.clearRetry()
    if (this.searchDebounce) clearTimeout(this.searchDebounce)
  },

  methods: {

    // ── Image des pages concurrentes ─────────────────────────────────────────
    // Les URLs CDN Facebook/Instagram stockées en ES expirent.
    // graph.facebook.com/{id}/picture est permanent et ne nécessite pas d'auth.
    _freshPicture: function(network, networkId, fallback) {
      // graph.facebook.com/{id}/picture fonctionne pour les pages Facebook (ID numérique ou username).
      // Pour Instagram les concurrents sont identifiés par username, pas par un ID Meta valide —
      // on retourne directement le fallback (image stockée lors de la recherche).
      if (network === 'facebook' && networkId && networkId !== '__own__') {
        return 'https://graph.facebook.com/' + networkId + '/picture?type=large'
      }
      return fallback || null
    },

    rowPicture: function(row) {
      return this._freshPicture(row.network, row.network_id, row.concurrent_picture)
    },

    onImgError: function(networkId) {
      this.$set(this.brokenImages, networkId, true)
    },

    initLocalConcurrents: function() {
      if (!this.page || !this.page.reporting || !this.page.reporting.concurrents) {
        this.localConcurrents = []
        return
      }
      this.localConcurrents = this.page.reporting.concurrents.map(function(c) {
        try { return typeof c === 'string' ? JSON.parse(c) : c } catch (_) { return null }
      }).filter(Boolean)
    },

    // --- Search ---

    onSearchInput: function() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce)
      this.searchResults = []
      if (this.searchQuery.length < 2) return
      var self = this
      this.searchDebounce = setTimeout(function() { self.doSearch() }, 800)
    },

    async doSearch() {
      if (!this.page || this.searchQuery.length < 2) return
      this.searchLoading = true
      this.searchResults = []
      var apiHost = process.env.VUE_APP_API_HOST
      var token = localStorage.getItem('token')
      try {
        var q = encodeURIComponent(this.searchQuery)
        var url = apiHost + '/bots/mentions?id=' + this.page.id + '&search=' + q + '&url=false&simulate=false'
        var res = await fetch(url, { headers: { Authorization: token ? ('Bearer ' + token) : '' } })
        var body = await res.json()
        if (!body || !body.mentions) { return }
        var net = this.network
        var results = Object.values(body.mentions).map(function(m) {
          if (net === 'facebook') {
            return {
              id: m.id,
              name: m.name,
              image: m.picture && m.picture.data && m.picture.data.url ? m.picture.data.url : null,
              description: m.subtext || null,
            }
          }
          if (net === 'twitter') {
            return {
              id: m.id || m.id_str,
              name: m.name,
              unique_id: m.username || m.screen_name || null,
              image: m.profile_image_url || m.profile_image_url_https || null,
              description: m.description || m.location || null,
            }
          }
          if (net === 'linkedin') {
            return {
              id: m.id,
              name: m.name,
              image: m.image || null,
              description: m.subtitle || m.description || null,
            }
          }
          if (net === 'instagram') {
            return {
              id: m.id,
              name: m.name,
              unique_id: m.username || m.unique_id || null,
              image: m.image || null,
              description: m.biography || null,
            }
          }
          if (net === 'tiktok') {
            return {
              id: m.id,
              unique_id: m.unique_id,
              name: m.name,
              image: m.image || null,
            }
          }
          return null
        }).filter(Boolean)
        this.searchResults = results

        // Facebook: charger les avatars manquants en secondaire
        if (net === 'facebook') {
          var self = this
          results.forEach(function(result, i) {
            if (!result.image) {
              var avatarUrl = apiHost + '/bots/mentions/avatar?id=' + self.page.id + '&mention=' + result.id
              fetch(avatarUrl, { headers: { Authorization: token ? ('Bearer ' + token) : '' } })
                .then(function(r) { return r.json() })
                .then(function(data) {
                  if (data && data.avatar && data.avatar.data && data.avatar.data.url) {
                    self.$set(self.searchResults[i], 'image', data.avatar.data.url)
                  }
                })
                .catch(function() {})
            }
          })
        }
      } catch (e) {
        this.searchResults = []
      } finally {
        this.searchLoading = false
      }
    },

    async selectResult(mention) {
      if (this.addLoading) return
      // Éviter les doublons
      var existing = this.localConcurrents.find(function(c) {
        return (mention.id && (c.id === mention.id || c.id === String(mention.id))) ||
               (mention.unique_id && c.unique_id === mention.unique_id) ||
               (mention.name && !mention.id && c.name === mention.name)
      })
      if (existing) { this.showSearch = false; return }

      this.addLoading = true
      try {
        var apiHost = process.env.VUE_APP_API_HOST
        var token = localStorage.getItem('token')
        var res = await fetch(apiHost + '/reporting/concurrent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: token ? ('Bearer ' + token) : '' },
          body: JSON.stringify({ pageId: this.page.id, concurrent: mention }),
        })
        if (res.ok) {
          this.localConcurrents.push(mention)
          this.showSearch = false
          this.searchQuery = ''; this.searchResults = []
          // Reload display data to include new concurrent
          this.concurrents = []; this.load()
        }
      } catch (e) {
        console.error('addConcurrent error', e)
      } finally {
        this.addLoading = false
      }
    },

    async removeConcurrent(row) {
      var concurrentKey = row.network_id
      this.$set(this.removeLoading, concurrentKey, true)
      try {
        var apiHost = process.env.VUE_APP_API_HOST
        var token = localStorage.getItem('token')
        var url = apiHost + '/reporting/concurrent?pageId=' + this.page.id + '&concurrent=' + encodeURIComponent(concurrentKey)
        var res = await fetch(url, { method: 'DELETE', headers: { Authorization: token ? ('Bearer ' + token) : '' } })
        if (res.ok) {
          // Remove from local list and display list immediately
          this.localConcurrents = this.localConcurrents.filter(function(c) {
            return String(c.id) !== String(concurrentKey) && c.unique_id !== concurrentKey && c.name !== concurrentKey
          })
          this.concurrents = this.concurrents.filter(function(c) { return c.network_id !== concurrentKey })
          this.$delete(this.expanded, concurrentKey)
        }
      } catch (e) {
        console.error('removeConcurrent error', e)
      } finally {
        this.$delete(this.removeLoading, concurrentKey)
      }
    },

    // --- Display data ---

    async load() {
      if (!this.page || !this.hasAnyConcurrent) return
      this.loading = true
      this.error = null
      try {
        var apiHost = process.env.VUE_APP_API_HOST
        var since = moment(this.dateStart).unix()
        // Fin du dernier jour inclus (minuit excluait les posts du dernier jour de la période)
        var until = moment(this.dateEnd).endOf('day').unix()
        var url = apiHost + '/reporting/concurrent/data?page_id=' + this.page.id + '&since=' + since + '&until=' + until
        var token = localStorage.getItem('token')
        var res = await fetch(url, { headers: { Authorization: token ? ('Bearer ' + token) : '' } })
        var json = await res.json()
        if (!res.ok) throw new Error(json.message || 'Erreur serveur')
        this.concurrents = json.concurrents || []
        var self = this
        var hasPending = this.concurrents.some(function(c) { return c.pending })
        if (hasPending) {
          this.retryTimer = setTimeout(function() { self.load() }, 8000)
        }
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },

    clearRetry: function() {
      if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null }
    },

    toggle: function(id) {
      var isOpen = !this.expanded[id]
      this.$set(this.expanded, id, isOpen)
      if (isOpen && !this.pagination[id]) this.$set(this.pagination, id, 1)
    },

    // --- Table sort ---

    onSort: function(key) {
      if (this.sortKey === key) { this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc' }
      else { this.sortKey = key; this.sortDir = 'desc' }
    },
    sortClass: function(key) { return { 'col-active': this.sortKey === key } },
    sortIcon: function(key) {
      if (this.sortKey !== key) return 'unfold_more'
      return this.sortDir === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
    },
    sortVal: function(row, key) {
      if (key === 'name') return (row.concurrent_name || row.network_id || '').toLowerCase()
      if (key === 'followers') return this.rowFollowers(row)
      if (key === 'posts') return this.rowPostsCount(row)
      if (key === 'reactions') return this.rowReactions(row)
      if (key === 'comments') return this.rowComments(row)
      if (key === 'shares') return this.rowShares(row)
      if (key === 'engagement') {
        var f = this.rowFollowers(row); var n = this.rowPostsCount(row)
        return (f && n) ? ((this.rowReactions(row) + this.rowComments(row) + this.rowShares(row)) / n / f) : 0
      }
      return 0
    },

    // --- Post sort ---

    getPostSort: function(id) { return this.postSort[id] || { key: 'date', dir: 'desc' } },
    onPostSort: function(id, key) {
      var current = this.getPostSort(id)
      var dir = (current.key === key) ? (current.dir === 'desc' ? 'asc' : 'desc') : 'desc'
      this.$set(this.postSort, id, { key: key, dir: dir })
      this.$set(this.pagination, id, 1)
    },
    postSortClass: function(id, key) { return { 'col-active': this.getPostSort(id).key === key } },
    postSortIcon: function(id, key) {
      var s = this.getPostSort(id)
      if (s.key !== key) return 'unfold_more'
      return s.dir === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
    },

    // --- Pagination ---

    getPage: function(id) { return this.pagination[id] || 1 },
    setPage: function(id, p) { this.$set(this.pagination, id, p) },
    postPageCount: function(row) { return Math.ceil(this.sortedPosts(row).length / PAGE_SIZE) },
    sortedPosts: function(row) {
      var s = this.getPostSort(row.network_id)
      var dir = s.dir === 'asc' ? 1 : -1
      return this.rowPosts(row).slice().sort(function(a, b) {
        var va, vb
        if (s.key === 'reactions') {
          va = a.reactions && a.reactions.summary ? a.reactions.summary.total_count : (a.like_count || a.reactions_count || 0)
          vb = b.reactions && b.reactions.summary ? b.reactions.summary.total_count : (b.like_count || b.reactions_count || 0)
        } else if (s.key === 'comments') {
          va = a.comments && a.comments.summary ? a.comments.summary.total_count : (a.comments_count || 0)
          vb = b.comments && b.comments.summary ? b.comments.summary.total_count : (b.comments_count || 0)
        } else if (s.key === 'shares') {
          va = a.shares && a.shares.count ? a.shares.count : 0
          vb = b.shares && b.shares.count ? b.shares.count : 0
        } else {
          va = new Date(a.created_time || a.timestamp || a.createdAt || 0)
          vb = new Date(b.created_time || b.timestamp || b.createdAt || 0)
        }
        if (va < vb) return -1 * dir
        if (va > vb) return 1 * dir
        return 0
      })
    },
    paginatedPosts: function(row) {
      var page = this.getPage(row.network_id)
      var start = (page - 1) * PAGE_SIZE
      return this.sortedPosts(row).slice(start, start + PAGE_SIZE)
    },

    // --- Row accessors ---

    rowPosts: function(row) {
      if (row.isOwnPage || !row.posts) return []
      if (Array.isArray(row.posts)) return row.posts
      try { return JSON.parse(row.posts) } catch (_) { return [] }
    },
    rowFollowers: function(row) { return row.pageFollowers || row.pageFans || 0 },
    rowPostsCount: function(row) {
      if (row.isOwnPage) return row.postsCount || 0
      return this.rowPosts(row).length
    },
    rowReactions: function(row) {
      if (row.total_reactions != null) return row.total_reactions
      return this.rowPosts(row).reduce(function(s, p) {
        return s + (p.reactions && p.reactions.summary ? p.reactions.summary.total_count : (p.like_count || p.reactions_count || 0))
      }, 0)
    },
    rowComments: function(row) {
      if (row.total_comments != null) return row.total_comments
      return this.rowPosts(row).reduce(function(s, p) {
        return s + (p.comments && p.comments.summary ? p.comments.summary.total_count : (p.comments_count || 0))
      }, 0)
    },
    rowShares: function(row) {
      if (row.total_shares != null) return row.total_shares
      return this.rowPosts(row).reduce(function(s, p) { return s + (p.shares && p.shares.count ? p.shares.count : 0) }, 0)
    },
    rowEngagement: function(row) {
      var f = this.rowFollowers(row); var n = this.rowPostsCount(row)
      var eng = this.rowReactions(row) + this.rowComments(row) + this.rowShares(row)
      if (!f || !n) return '—'
      return ((eng / n / f) * 1000).toFixed(2)
    },

    // --- Post-level helpers ---

    postDate: function(p) {
      var ts = p.created_time || p.timestamp || (p.createdAt ? new Date(p.createdAt).toISOString() : null)
      return ts ? moment(ts).format('DD/MM/YY') : '—'
    },
    postMessage: function(p) {
      var msg = p.message || p.caption || p.text || p.commentary || ''
      return msg.length > 120 ? msg.slice(0, 120) + '…' : msg
    },
    postLink: function(p) { return p.permalink_url || p.permalink || null },
    postReactions: function(p) {
      if (p.reactions && p.reactions.summary) return p.reactions.summary.total_count
      return p.like_count != null ? p.like_count : (p.reactions_count || 0)
    },
    postComments: function(p) {
      if (p.comments && p.comments.summary) return p.comments.summary.total_count
      return p.comments_count || 0
    },
    postShares: function(p) { return p.shares && p.shares.count != null ? p.shares.count : 0 },

    fmt: function(n) {
      if (n == null || n === '') return '—'
      return Number(n).toLocaleString('fr-FR')
    },
    cap: function(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },

    // Called by ReportingMain before PDF export — waits for data to be ready
    getConcurrentDataForPdf: async function() {
      if (!this.hasAnyConcurrent) return []
      if (this.concurrents.length > 0 && !this.loading) {
        return this.sortedRows.filter(function(r) { return !r.pending && !r.error })
      }
      var self = this
      if (!this.loading) {
        await this.load()
      } else {
        await new Promise(function(resolve) {
          var unwatch = self.$watch('loading', function(val) {
            if (!val) { unwatch(); resolve() }
          })
        })
      }
      return this.sortedRows.filter(function(r) { return !r.pending && !r.error })
    },
  },
}
</script>

<style scoped>
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.section-icon { color: #6B1F4D; font-size: 22px; }
.section-header h2 { font-size: 16px; font-weight: 700; color: #121826; margin: 0; flex: 1; }

/* Add button */
.add-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border-radius: 20px;
  border: 1.5px solid #e0d0ea;
  background: #fff;
  color: #6B1F4D;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
}
.add-btn:hover { background: #f3eaf7; border-color: #c0a8d0; }
.add-btn.active { background: #6B1F4D; color: #fff; border-color: #6B1F4D; }
.add-btn .material-icons { font-size: 15px; }

/* Search panel */
.search-panel {
  margin-bottom: 16px;
  border: 1px solid #e8e0f0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(107,31,77,0.06);
}
.search-input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  background: #fdf9fc;
}
.search-icon { color: #c0a8d0; font-size: 18px; flex-shrink: 0; }
.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 13px;
  color: #121826;
  outline: none;
}
.search-input::placeholder { color: #c0a8d0; }
.search-spinner { display: flex; align-items: center; }
.input-loader {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid #e0d0ea;
  border-top-color: #6B1F4D;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

.search-results { border-top: 1px solid #f1edf5; background: #fff; }
.search-result {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.1s;
  border-bottom: 1px solid #f9f7fb;
}
.search-result:last-child { border-bottom: none; }
.search-result:hover { background: #fdf4ff; }
.result-thumb {
  width: 36px; height: 36px; border-radius: 50%; overflow: hidden;
  background: #f1edf5; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; color: #c0a8d0;
}
.result-thumb img { width: 100%; height: 100%; object-fit: cover; }
.result-thumb .material-icons { font-size: 20px; }
.result-info { flex: 1; min-width: 0; }
.result-row1 { display: flex; align-items: baseline; gap: 6px; }
.result-name { font-size: 13px; font-weight: 600; color: #121826; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.result-handle { font-size: 11px; color: #b0a0c0; white-space: nowrap; flex-shrink: 0; }
.result-desc { font-size: 11px; color: #b0a0c0; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.result-add {
  color: #6B1F4D; font-size: 16px;
  opacity: 0; transition: opacity 0.1s;
  flex-shrink: 0;
}
.search-result:hover .result-add { opacity: 0.7; }

/* Loading skeletons */
.search-loading-results { border-top: 1px solid #f1edf5; background: #fff; }
.search-skeleton {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 12px;
}
.skel-thumb {
  width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(90deg, #f5f0f9 25%, #ece4f4 50%, #f5f0f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
}
.skel-lines { flex: 1; display: flex; flex-direction: column; gap: 5px; }
.skel-line {
  height: 9px; border-radius: 5px;
  background: linear-gradient(90deg, #f5f0f9 25%, #ece4f4 50%, #f5f0f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
}
.skel-line--wide { width: 55%; }
.skel-line--narrow { width: 30%; }
@keyframes shimmer { to { background-position: -200% 0; } }

.search-empty { padding: 12px 14px; font-size: 12px; color: #c0a8d0; border-top: 1px solid #f1edf5; background: #fff; }

/* Empty state */
.post-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 0;
  color: #9ba3b7;
}
.post-empty .material-icons { font-size: 40px; color: #e0d0ea; }
.post-empty p { font-size: 14px; }

/* Table */
.concurrent-table { width: 100%; overflow-x: auto; }
.table-head {
  display: grid;
  gap: 8px;
  align-items: center;
  padding: 8px 4px 10px;
  border-bottom: 2px solid #f1edf5;
}
.row-group { border-bottom: 1px solid #f9f7fb; }
.row-group:last-child { border-bottom: none; }
.table-row {
  display: grid;
  gap: 8px;
  align-items: center;
  padding: 10px 4px;
  font-size: 13px;
  color: #121826;
  border-radius: 5.6px;
  transition: background 0.12s;
}
.table-row:not(.row--own):not(.row--pending):not(.row--error) { cursor: pointer; }
.table-row:not(.row--own):not(.row--pending):not(.row--error):hover { background: #fdf9fc; }

/* Own page */
.row--own {
  background: linear-gradient(90deg, rgba(107,31,77,0.07) 0%, rgba(107,31,77,0.02) 100%);
  border-left: 3px solid #6B1F4D;
  padding-left: 6px;
}
.row--own .page-name { color: #6B1F4D; font-weight: 700; }
.row--own .cell-num { font-weight: 600; color: #6B1F4D; }

/* Column headers */
.col-header {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  letter-spacing: 0.2px;
  white-space: nowrap;
  user-select: none;
}
.col-header.sortable { cursor: pointer; }
.col-header.sortable:hover { color: #6B1F4D; }
.col-header.col-active { color: #6B1F4D; }
.sort-icon { font-size: 14px; opacity: 0.6; }
.col-active .sort-icon { opacity: 1; }
.align-right { justify-content: flex-end; text-align: right; }

/* Page cell */
.cell-page { display: flex; align-items: center; gap: 8px; min-width: 0; }
.page-thumb {
  width: 30px; height: 30px; flex-shrink: 0;
  border-radius: 50%; overflow: hidden;
  background: #f1edf5;
  display: flex; align-items: center; justify-content: center; color: #c0a8d0;
}
.page-thumb img { width: 100%; height: 100%; object-fit: cover; }
.page-thumb .material-icons { font-size: 18px; }
.page-name { font-size: 13px; font-weight: 600; color: #121826; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; min-width: 0; }
.expand-icon { font-size: 16px; color: #c0a8d0; margin-left: auto; flex-shrink: 0; }

.cell-num { text-align: right; font-size: 13px; font-weight: 500; min-width: 0; }

/* Delete button */
.cell-actions { display: flex; align-items: center; justify-content: center; }
.delete-btn {
  display: flex; align-items: center; justify-content: center;
  width: 20px; height: 20px;
  border: none;
  background: none;
  color: #d0b8e0;
  cursor: pointer;
  transition: color 0.12s, opacity 0.12s;
  opacity: 0;
  padding: 0;
}
.table-row:hover .delete-btn { opacity: 1; }
.delete-btn:hover { color: #c0392b; }
.delete-btn.loading { opacity: 1; color: #d0b8e0; cursor: not-allowed; }
.delete-btn .material-icons { font-size: 15px; }

/* Type badges */
.type-badge {
  display: inline-flex; align-items: center;
  padding: 1px 7px; border-radius: 10px;
  font-size: 10px; font-weight: 600;
  letter-spacing: 0.2px; white-space: nowrap; max-width: fit-content;
}
.badge-pending { background: #fdf4ff; color: #6B1F4D; }
.badge-error { background: #fdf2f2; color: #c0392b; }
.badge-cache { background: #f1f5f9; color: #9ba3b7; }

/* Sub-section */
.sub-section {
  margin: 2px 4px 10px;
  border: 1px solid #f1edf5;
  border-radius: 8px;
  overflow: hidden;
  background: #fdfcfe;
}
.post-head, .post-row {
  display: grid; gap: 8px; align-items: center; padding: 7px 16px;
}
.post-head { border-bottom: 1px solid #f1edf5; padding-bottom: 8px; }
.post-row { border-bottom: 1px solid #f9f7fb; font-size: 12px; color: #121826; }
.post-row:last-child { border-bottom: none; }
.post-row:hover { background: rgba(18,24,38,0.015); }

.cell-date { color: #9ba3b7; font-size: 12px; white-space: nowrap; }
.cell-post { display: flex; align-items: center; gap: 6px; min-width: 0; }
.post-msg { font-size: 12px; color: #555E75; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; min-width: 0; }
.post-link { color: #c0a8d0; display: flex; align-items: center; flex-shrink: 0; text-decoration: none; }
.post-link:hover { color: #6B1F4D; }
.post-link .material-icons { font-size: 14px; }

/* Pagination */
.pagination { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 10px 16px; border-top: 1px solid #f1edf5; }
.page-btn {
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 8px;
  border: 1px solid #e0d0ea; background: #fff; color: #6B1F4D;
  cursor: pointer; transition: all 0.15s;
}
.page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.page-btn:not(:disabled):hover { background: #f1edf5; }
.page-btn .material-icons { font-size: 18px; }
.page-info { font-size: 13px; color: #555E75; font-weight: 500; min-width: 60px; text-align: center; }

.spin { animation: spin 2s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
